import { useState, useEffect, useCallback } from 'react';
import { Waves, RefreshCw, TrendingUp, TrendingDown, AlertTriangle, Minus } from 'lucide-react';
import ModuleContainer from '../components/layout/ModuleContainer.jsx';
import { usePage } from '../contexts/PageContext.jsx';

// Sprint 22.7.26 (Tier 1 greenfield, Module 10): Cross-Market Butterfly Engine.
// Surfaces GET /api/butterfly (butterflyService): obscure cross-asset ratios +
// volatility-structure signals that tend to lead the headline indices.

const SIGNAL = {
  rising: { color: '#10b981', icon: TrendingUp },
  falling: { color: '#ef4444', icon: TrendingDown },
  stress: { color: '#ef4444', icon: AlertTriangle },
  elevated: { color: '#f59e0b', icon: AlertTriangle },
  calm: { color: '#10b981', icon: Minus },
  normal: { color: '#6b7280', icon: Minus },
  flat: { color: '#6b7280', icon: Minus },
};

function IndicatorCard({ ind }) {
  if (!ind.available) {
    return (
      <div className="rounded-lg border border-korinth-border/20 bg-korinth-bg/30 p-3 flex items-center gap-2">
        <span className="text-sm text-korinth-muted">{ind.name}</span>
        <span className="text-[10px] text-korinth-muted/60 ml-auto">{ind.note || 'unavailable'}</span>
      </div>
    );
  }
  const s = SIGNAL[ind.signal] || SIGNAL.flat;
  const Icon = s.icon;
  return (
    <div className="rounded-lg border p-3" style={{ borderColor: `${s.color}40`, backgroundColor: `${s.color}0a` }}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm font-bold text-white">{ind.name}</span>
        <span className="text-[10px] font-mono text-korinth-muted">{ind.label}</span>
        <span className="text-[11px] font-mono text-korinth-text">{ind.value}</span>
        {ind.trend_pct != null && (
          <span className="text-[10px] font-mono" style={{ color: ind.trend_pct > 0 ? '#10b981' : ind.trend_pct < 0 ? '#ef4444' : '#6b7280' }}>
            {ind.trend_pct > 0 ? '+' : ''}{ind.trend_pct}%
          </span>
        )}
        <span className="ml-auto flex items-center gap-1 text-[10px] font-mono font-bold uppercase px-1.5 py-0.5 rounded" style={{ color: s.color, backgroundColor: `${s.color}1f` }}>
          <Icon className="w-3 h-3" />{ind.signal}
        </span>
      </div>
      <div className="text-[11px] text-korinth-text mt-1.5">{ind.interpretation}</div>
    </div>
  );
}

export default function ButterflyEngine() {
  const { setPage } = usePage();
  useEffect(() => { setPage('butterfly'); }, []);

  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const r = await fetch('/api/butterfly');
      const d = await r.json();
      if (!r.ok) { setErr(d.error || `HTTP ${r.status}`); return; }
      setData(d);
    } catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const iv = setInterval(load, 120000);
    return () => clearInterval(iv);
  }, [load]);

  const indicators = data?.indicators || [];

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 flex-wrap">
        <Waves className="w-6 h-6 text-korinth-accent" />
        <h1 className="text-2xl font-bold text-white">Cross-Market Butterfly</h1>
        <span className="text-korinth-muted text-sm">Obscure cross-asset ratios + vol structure that tend to lead the headline indices ({data?.window_days ?? 5}d trend)</span>
        <button onClick={load} className="ml-auto flex items-center gap-1.5 text-[11px] font-mono px-2.5 py-1.5 rounded border border-korinth-border/40 text-korinth-text hover:bg-korinth-border/40 transition-colors">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />Refresh
        </button>
      </div>

      {err && <div className="text-xs text-red-400 font-mono">{err}</div>}
      {data && <div className="text-[10px] text-korinth-muted font-mono">{data.signaling_count} of {indicators.filter(i => i.available).length} indicators signaling - as of {data.as_of ? new Date(data.as_of).toLocaleString() : '-'}</div>}

      <ModuleContainer title="Leading indicators" subtitle="Each shows the current reading, its 5-day trend, and what it implies before the indices move">
        {indicators.length === 0 ? (
          <div className="text-[11px] text-korinth-muted">{loading ? 'Loading...' : 'No indicators.'}</div>
        ) : (
          <div className="space-y-2.5">
            {indicators.map((ind) => <IndicatorCard key={ind.key} ind={ind} />)}
          </div>
        )}
      </ModuleContainer>
    </div>
  );
}
